// Typed client for the three structured-clinical-capture endpoint families —
// see HMS_V4_API_CONTRACT.md §6. Deliberately built on lib/api/client.ts's
// `api`, not the legacy src/api.js (whose patientAllergies/prescriptions
// wrappers have no ETag/If-Match support).
import { api, type Versioned } from '@/lib/api/client';
import type {
  Allergy, AllergyInput,
  EncounterDiagnosis, EncounterDiagnosisInput,
  Prescription, PrescriptionInput, PrescriptionItemInput,
  PrescriptionStatus,
  MedicineSearchResult,
  MedicineBrandResult,
  FormularyStatusResponse,
  SafetyAlert,
} from './types';

// ── Allergies — /api/patients/:uhid/allergies, tenant-wide ─────────

export function listAllergies(uhid: string, includeInactive = false): Promise<Allergy[]> {
  return api.get(`/patients/${uhid}/allergies`, { query: { include_inactive: includeInactive ? 1 : undefined } });
}

export function createAllergy(uhid: string, input: AllergyInput): Promise<Allergy> {
  return api.post(`/patients/${uhid}/allergies`, input);
}

/**
 * `ifMatch` is opportunistic, not required — the backend's ifMatchOk() treats
 * a missing If-Match as always valid (apiResponse.js), so omitting it (e.g.
 * editing a row straight from a list that carries no per-row etag) is safe.
 */
export function updateAllergy(uhid: string, id: number, patch: Partial<AllergyInput>, ifMatch?: string): Promise<Allergy> {
  return api.patch(`/patients/${uhid}/allergies/${id}`, patch, { ifMatch });
}

export function retractAllergy(uhid: string, id: number): Promise<{ retracted: true; allergy: Allergy }> {
  return api.delete(`/patients/${uhid}/allergies/${id}`);
}

// ── Encounter diagnoses — /api/visits/:visitCode/diagnoses ─────────
// :visitCode is the visit's code (VIS-...), not its numeric id — matches
// every other route keyed off a visit in routes/visits.js.

export function listVisitDiagnoses(visitCode: string): Promise<EncounterDiagnosis[]> {
  return api.get(`/visits/${visitCode}/diagnoses`);
}

export function createDiagnosis(visitCode: string, input: EncounterDiagnosisInput): Promise<EncounterDiagnosis> {
  return api.post(`/visits/${visitCode}/diagnoses`, input);
}

export function updateDiagnosis(visitCode: string, id: number, patch: Partial<EncounterDiagnosisInput>, ifMatch?: string): Promise<EncounterDiagnosis> {
  return api.patch(`/visits/${visitCode}/diagnoses/${id}`, patch, { ifMatch });
}

export function retractDiagnosis(visitCode: string, id: number): Promise<{ retracted: true; diagnosis: EncounterDiagnosis }> {
  return api.delete(`/visits/${visitCode}/diagnoses/${id}`);
}

/**
 * Every diagnosis this patient has ever had, across every visit and branch —
 * tenant-wide, same reasoning as listPrescriptionsForPatient below. Rows
 * carry visit_code/visit_started_at and are ordered by visit date, newest
 * first — what PrescriptionBuilder-style "repeat from last visit" chips need
 * to find the most recent *other* visit's diagnoses.
 */
export function listDiagnosesForPatient(uhid: string): Promise<EncounterDiagnosis[]> {
  return api.get(`/visits/patient/${uhid}/diagnoses`);
}

// ── Prescriptions — /api/prescriptions, branch-scoped writes ───────

/**
 * Checked before creating a new draft on mount, so refreshing mid-
 * consultation resumes the same prescription instead of spawning a second
 * one for the same visit.
 */
export function listPrescriptionsForVisit(visitId: number): Promise<Prescription[]> {
  return api.get('/prescriptions', { query: { visit_id: visitId } });
}

export function createPrescription(input: PrescriptionInput): Promise<Prescription> {
  return api.post('/prescriptions', input);
}

/** Versioned so a caller about to PUT/PATCH can pass the fresh etag as ifMatch. */
export function getPrescriptionVersioned(id: number): Promise<Versioned<Prescription>> {
  return api.getVersioned(`/prescriptions/${id}`);
}

export function savePrescription(id: number, patch: { notes?: string | null; status?: PrescriptionStatus; items?: PrescriptionInput['items'] }, ifMatch?: string): Promise<Prescription> {
  return api.put(`/prescriptions/${id}`, patch, { ifMatch });
}

export function setPrescriptionStatus(id: number, status: PrescriptionStatus, reason?: string, ifMatch?: string): Promise<Prescription> {
  return api.patch(`/prescriptions/${id}/status`, { status, reason }, { ifMatch });
}

/**
 * Records the lines "Draft with AI" just proposed and returns one id per
 * line, in order, to carry on each row as `ai_proposal_id`. Instrumentation
 * only — callers must treat a failure as non-fatal.
 */
export function recordPrescriptionAiProposals(input: {
  patient_id: number;
  visit_id?: number | null;
  lines: PrescriptionItemInput[];
}): Promise<{ ids: number[] }> {
  return api.post('/prescriptions/ai-proposals', input);
}

/**
 * Every prescription this patient has ever had, across every visit and
 * branch — tenant-wide, per the file header's scoping note (a patient's
 * clinical history is visible at any branch). Each row carries `item_count`
 * only, not `items` — same summary-vs-detail split as GET / (list); fetch
 * `getPrescriptionVersioned(id)` for one prescription's medicine lines.
 */
export function listPrescriptionsForPatient(uhid: string): Promise<Prescription[]> {
  return api.get(`/prescriptions/patient/${uhid}`);
}

/**
 * A deliberately narrow subset of setPrescriptionStatus above — can only
 * ever move an ACTIVE prescription to 'completed' or 'cancelled' (reason
 * required for cancel), never reactivate or edit items. See
 * routes/prescriptions.js's PATCH /:id/close for why that narrowness is
 * what makes this safe to call from Pharmacy as well as the prescriber
 * modules — features/prescriptions/PatientPrescriptionHistory.tsx is the
 * only caller.
 */
export function closePrescription(id: number, status: 'completed' | 'cancelled', reason?: string): Promise<Prescription> {
  return api.patch(`/prescriptions/${id}/close`, { status, reason });
}

/**
 * Runs the same deterministic allergy/duplicate-therapy check the backend
 * runs on save — WITHOUT saving anything — so the doctor can be warned while
 * still typing, before a prescription row exists at all. Pass
 * excludePrescriptionId when checking an in-progress edit to an existing
 * prescription so it isn't compared against its own pre-edit rows.
 */
export function checkPrescriptionSafety(
  patientId: number,
  items: PrescriptionItemInput[],
  excludePrescriptionId?: number
): Promise<{ alerts: SafetyAlert[] }> {
  return api.post('/prescriptions/check', {
    patient_id: patientId,
    items,
    exclude_prescription_id: excludePrescriptionId,
  });
}

// ── Medicines master search — /api/medicines, tenant-wide ──────────
// Backs the drug-name and brand-name autocomplete on a prescription line.

/**
 * field: 'generic' restricts the match to generic_name and dedupes to one
 * row per distinct drug (DrugNameCell's picker) — omit it for the
 * brand-inclusive search BrandNameCell falls back to before a drug has been
 * chosen (doctors in India generally prescribe by brand first).
 */
export function searchMedicines(q: string, opts: { field?: 'generic' } = {}): Promise<MedicineSearchResult[]> {
  if (!q.trim()) return Promise.resolve([]);
  return api.get('/medicines', { query: { q, ...(opts.field ? { field: opts.field } : {}) } });
}

/**
 * Every known brand of one generic drug, branch-stock-annotated — see that
 * route's own comment. `drugId`, when known (Phase 3 — set once the generic
 * search result resolved one), gives an exact medicines(id, drug_id) match
 * instead of the generic-name ILIKE fallback, and is what makes the
 * formulary-aware sort/`formulary_status` on each result reliable.
 */
export function fetchBrandsForGeneric(generic: string, drugId?: number | null): Promise<MedicineBrandResult[]> {
  if (!generic.trim() && !drugId) return Promise.resolve([]);
  return api.get('/medicines/brands', { query: { generic, drug_id: drugId || undefined } });
}

/**
 * Advisory-only formulary status for one clinical drug — never a reason to
 * block prescribing (§23/§29 of the Drug Master architecture doc). Absent
 * `formulary` means allowed/unflagged, the common case.
 */
export function getFormularyStatus(drugId: number): Promise<FormularyStatusResponse> {
  return api.get('/formulary/status', { query: { drug_id: drugId } });
}
